import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchBlogBySlug, fetchBlogs } from '../../api/blogClient';
import PageMeta from '../../components/SEO/PageMeta';
import { SITE_NAME } from '../../config/site';
import { Blog } from '../../types/blog';
import styles from './BlogPage.module.css';

const DEFAULT_THUMBNAIL = '/blog-default-thumbnail.svg';

type LoadState<T> = {
  data: T;
  loading: boolean;
  error: string;
};

function getThumbnail(blog: Blog): string {
  return blog.thumbnailUrl || DEFAULT_THUMBNAIL;
}

function getPlainText(html: string): string {
  if (typeof document !== 'undefined') {
    const node = document.createElement('div');
    node.innerHTML = html;
    return node.textContent || '';
  }

  return html.replace(/<[^>]+>/g, ' ');
}

function getExcerpt(blog: Blog): string {
  const text = getPlainText(blog.htmlContent).replace(/\s+/g, ' ').trim();
  return text ? `${text.slice(0, 170)}${text.length > 170 ? '...' : ''}` : 'Read the full story.';
}

function getBlogCategory(blog: Blog): string {
  return blog.category || 'Uncategorized';
}

function groupBlogsByCategory(blogs: Blog[]): Array<{ category: string; blogs: Blog[] }> {
  const groups = blogs.reduce<Record<string, Blog[]>>((accumulator, blog) => {
    const category = getBlogCategory(blog);
    accumulator[category] = [...(accumulator[category] || []), blog];
    return accumulator;
  }, {});

  return Object.entries(groups)
    .map(([category, categoryBlogs]) => ({ category, blogs: categoryBlogs }))
    .sort(
      (left, right) =>
        right.blogs.length - left.blogs.length || left.category.localeCompare(right.category),
    );
}

function sanitizeHtml(html: string): string {
  if (typeof window === 'undefined' || !html) return html;

  const scriptProtocol = ['javascript', ':'].join('');
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelectorAll('script, style, iframe, object, embed, link, meta').forEach((node) => {
    node.remove();
  });

  doc.body.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      const value = attribute.value.trim().toLowerCase();

      if (
        name.startsWith('on') ||
        ((name === 'href' || name === 'src') && value.startsWith(scriptProtocol))
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });

  return doc.body.innerHTML;
}

function BlogImage({ blog, className }: { blog: Blog; className: string }) {
  return (
    <img
      className={className}
      src={getThumbnail(blog)}
      alt=""
      loading="lazy"
      onError={(event) => {
        if (event.currentTarget.src.endsWith(DEFAULT_THUMBNAIL)) return;
        event.currentTarget.src = DEFAULT_THUMBNAIL;
      }}
    />
  );
}

export default function BlogPage() {
  const { blogSlug } = useParams<{ blogSlug: string }>();

  if (blogSlug) {
    return <BlogDetail blogSlug={blogSlug} />;
  }

  return <BlogListing />;
}

function BlogListing() {
  const [state, setState] = useState<LoadState<Blog[]>>({
    data: [],
    loading: true,
    error: '',
  });

  useEffect(() => {
    let active = true;

    setState((current) => ({ ...current, loading: true, error: '' }));
    fetchBlogs()
      .then((blogs) => {
        if (!active) return;
        setState({ data: blogs, loading: false, error: '' });
      })
      .catch((error) => {
        if (!active) return;
        setState({
          data: [],
          loading: false,
          error: error instanceof Error ? error.message : 'Unable to load blogs right now.',
        });
      });

    return () => {
      active = false;
    };
  }, []);

  const blogGroups = useMemo(() => groupBlogsByCategory(state.data), [state.data]);

  return (
    <>
      <PageMeta
        title="Blog"
        description={`Read the latest ${SITE_NAME} guides, tips, and product updates.`}
        path="/blogs"
      />

      <section className={styles.listHero}>
        <p className={styles.eyebrow}>Blog</p>
        <h1>Ideas, guides, and updates</h1>
        <p>Practical articles from the {SITE_NAME} team, designed in a clean reading experience.</p>
      </section>

      <section className={styles.storyList} aria-label="Blog posts">
        {state.loading && <p className={styles.status}>Loading blog posts...</p>}
        {!state.loading && state.error && <p className={styles.error}>{state.error}</p>}
        {!state.loading && !state.error && state.data.length === 0 && (
          <p className={styles.status}>No blog posts are available yet.</p>
        )}

        {!state.loading &&
          !state.error &&
          blogGroups.map((group) => (
            <section key={group.category} className={styles.categorySection}>
              <div className={styles.categoryHeader}>
                <h2>{group.category}</h2>
                <span>
                  {group.blogs.length} post{group.blogs.length === 1 ? '' : 's'}
                </span>
              </div>
              <div className={styles.categoryGrid}>
                {group.blogs.map((blog) => (
                  <Link
                    key={blog.id || blog.slug}
                    to={`/blogs/${blog.slug}`}
                    className={styles.storyCard}
                  >
                    <span className={styles.storyText}>
                      <span className={styles.categoryBadge}>{getBlogCategory(blog)}</span>
                      <span className={styles.meta}>
                        <span>{blog.creator}</span>
                        <span aria-hidden>·</span>
                        <span>{blog.readingTime}</span>
                      </span>
                      <span className={styles.storyTitle}>{blog.title}</span>
                      <span className={styles.excerpt}>{getExcerpt(blog)}</span>
                    </span>
                    <BlogImage blog={blog} className={styles.storyImage} />
                  </Link>
                ))}
              </div>
            </section>
          ))}
      </section>
    </>
  );
}

function BlogDetail({ blogSlug }: { blogSlug: string }) {
  const [state, setState] = useState<LoadState<Blog | null>>({
    data: null,
    loading: true,
    error: '',
  });

  useEffect(() => {
    let active = true;

    setState({ data: null, loading: true, error: '' });
    fetchBlogBySlug(blogSlug)
      .then((blog) => {
        if (!active) return;
        setState({ data: blog, loading: false, error: '' });
      })
      .catch((error) => {
        if (!active) return;
        setState({
          data: null,
          loading: false,
          error: error instanceof Error ? error.message : 'Unable to load this blog post.',
        });
      });

    return () => {
      active = false;
    };
  }, [blogSlug]);

  const sanitizedContent = useMemo(
    () => sanitizeHtml(state.data?.htmlContent || ''),
    [state.data?.htmlContent],
  );

  if (state.loading) {
    return (
      <>
        <PageMeta title="Blog" description="Loading blog post." path={`/blogs/${blogSlug}`} />
        <p className={styles.status}>Loading blog post...</p>
      </>
    );
  }

  if (state.error || !state.data) {
    return (
      <>
        <PageMeta
          title="Blog"
          description="Unable to load blog post."
          path={`/blogs/${blogSlug}`}
        />
        <div className={styles.emptyState}>
          <p className={styles.error}>{state.error || 'Unable to load this blog post.'}</p>
          <Link to="/blogs">Back to all posts</Link>
        </div>
      </>
    );
  }

  const blog = state.data;
  const description = getExcerpt(blog);

  return (
    <>
      <PageMeta
        title={blog.title}
        description={description}
        path={`/blogs/${blog.slug}`}
        ogType="article"
      />

      <article className={styles.article}>
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <Link to="/blogs">Blog</Link>
          <span aria-hidden> / </span>
          <span>{blog.title}</span>
        </nav>

        <header className={styles.articleHeader}>
          <p className={styles.eyebrow}>Article</p>
          <h1>{blog.title}</h1>
          <div className={styles.byline}>
            <span>{blog.creator}</span>
            <span aria-hidden>·</span>
            <span>{blog.readingTime}</span>
          </div>
        </header>

        <BlogImage blog={blog} className={styles.heroImage} />

        <div
          className={styles.content}
          dangerouslySetInnerHTML={{ __html: sanitizedContent || '<p>No content available.</p>' }}
        />
      </article>
    </>
  );
}
